'use client';

import { useState, useEffect, useRef } from 'react';
import { X, Cookie, Shield, Settings } from 'lucide-react';
import Link from 'next/link';

type ConsentPreferences = {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
  preferences: boolean;
};

const defaultPreferences: ConsentPreferences = {
  necessary: true, // Always required
  analytics: false,
  marketing: false,
  preferences: false,
};

export default function CookieConsent() {
  const [isVisible, setIsVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState<ConsentPreferences>(defaultPreferences);
  const dialogRef = useRef<HTMLDivElement>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);

  useEffect(() => {
    // Check if user has already made a choice
    const consent = localStorage.getItem('cookie-consent');
    if (!consent) {
      // Small delay to prevent flash on page load
      const timer = setTimeout(() => setIsVisible(true), 1000);
      return () => clearTimeout(timer);
    } else {
      // Load saved preferences and apply them
      try {
        const savedPreferences = JSON.parse(consent);
        setPreferences(savedPreferences);
        applyConsentPreferences(savedPreferences);
      } catch {
        setIsVisible(true);
      }
    }
    return undefined;
  }, []);

  const applyConsentPreferences = (prefs: ConsentPreferences) => {
    // Enable/disable Google Analytics based on consent
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('consent', 'update', {
        'analytics_storage': prefs.analytics ? 'granted' : 'denied',
        'ad_storage': prefs.marketing ? 'granted' : 'denied',
        'ad_user_data': prefs.marketing ? 'granted' : 'denied',
        'ad_personalization': prefs.marketing ? 'granted' : 'denied',
        'functionality_storage': prefs.preferences ? 'granted' : 'denied',
        'personalization_storage': prefs.preferences ? 'granted' : 'denied',
        'security_storage': 'granted', // Always needed
      });
    }
    // Enable/disable Meta Pixel based on marketing consent
    if (typeof window !== 'undefined' && window.fbq) {
      window.fbq('consent', prefs.marketing ? 'grant' : 'revoke');
    }
  };

  const saveConsent = (prefs: ConsentPreferences) => {
    localStorage.setItem('cookie-consent', JSON.stringify(prefs));
    localStorage.setItem('cookie-consent-date', new Date().toISOString());
    applyConsentPreferences(prefs);
    setIsVisible(false);
  };

  const acceptAll = () => {
    const allAccepted: ConsentPreferences = {
      necessary: true,
      analytics: true,
      marketing: true,
      preferences: true,
    };
    setPreferences(allAccepted);
    saveConsent(allAccepted);
  };

  const acceptNecessary = () => {
    saveConsent(defaultPreferences);
  };

  const savePreferences = () => {
    saveConsent(preferences);
    setShowSettings(false);
  };

  // Focus management: when banner appears, remember current focus, focus
  // the first interactive element inside the dialog, and trap Tab. ESC
  // dismisses with "necessary only" (least surprising behavior). Restores
  // focus to the previously-focused element when the dialog closes.
  useEffect(() => {
    if (!isVisible) return;
    previouslyFocused.current = (document.activeElement as HTMLElement) || null;

    const focusables = () =>
      Array.from(
        dialogRef.current?.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        ) ?? []
      );

    // Focus first interactive element on open.
    setTimeout(() => focusables()[0]?.focus(), 0);

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        acceptNecessary();
        return;
      }
      if (e.key !== 'Tab') return;
      const items = focusables();
      if (items.length === 0) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      previouslyFocused.current?.focus?.();
    };
  }, [isVisible]);

  if (!isVisible) return null;

  const toggle = (checked: boolean, onChange: (v: boolean) => void, label: string) => (
    <label className="relative inline-flex cursor-pointer items-center">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="peer sr-only" aria-label={label} />
      <div className="h-6 w-11 rounded-full bg-al-cream/15 after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:bg-al-cream after:transition-all after:content-[''] peer-checked:bg-al-rose peer-checked:after:translate-x-full peer-focus-visible:ring-2 peer-focus-visible:ring-al-gold"></div>
    </label>
  );

  const rowClass = 'rounded-sm border border-al-line/80 p-4';

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 z-[9998] bg-black/40" aria-hidden="true" />

      {/* Cookie Banner (modal dialog) */}
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="cookie-consent-title"
        aria-describedby="cookie-consent-desc"
        className="fixed bottom-0 left-0 right-0 z-[9999] p-3 font-al-body sm:p-5">
        <div className="mx-auto max-w-3xl overflow-hidden rounded-sm border border-al-line bg-al-ink-soft text-al-cream shadow-2xl shadow-black/60">
          {!showSettings ? (
            <div className="p-5 sm:p-6">
              <div className="flex items-start gap-4">
                <Cookie className="mt-1 hidden h-6 w-6 shrink-0 text-al-gold sm:block" />
                <div className="flex-1">
                  <h3 id="cookie-consent-title" className="font-al-display text-2xl leading-tight">
                    Your privacy, respected
                  </h3>
                  <p id="cookie-consent-desc" className="mt-2 text-sm leading-relaxed text-al-cream/70">
                    We use cookies to run the store, understand what you love and personalise your visit. Read more in our{' '}
                    <Link href="/privacy" className="text-al-gold underline-offset-2 hover:underline">
                      Privacy Policy
                    </Link>
                    .
                  </p>
                  <div className="mt-4 flex flex-wrap items-center gap-2.5">
                    <button onClick={acceptAll} className="al-btn-primary px-5 py-2.5">
                      Accept all
                    </button>
                    <button onClick={acceptNecessary} className="al-btn-ghost px-5 py-2.5">
                      Necessary only
                    </button>
                    <button
                      onClick={() => setShowSettings(true)}
                      className="flex items-center gap-1.5 px-2 py-2.5 text-[11px] uppercase tracking-[0.18em] text-al-cream/70 hover:text-al-gold"
                    >
                      <Settings className="h-4 w-4" />
                      Customise
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="max-h-[80vh] overflow-y-auto p-5 sm:p-6">
              <div className="mb-5 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Shield className="h-6 w-6 text-al-gold" />
                  <h3 className="font-al-display text-2xl">Cookie preferences</h3>
                </div>
                <button
                  onClick={() => setShowSettings(false)}
                  className="rounded-sm p-2 text-al-cream/60 hover:text-al-gold"
                  aria-label="Close cookie preferences"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="mb-6 space-y-3">
                <div className={rowClass}>
                  <div className="mb-1.5 flex items-center justify-between">
                    <h4 className="text-sm font-medium">Necessary cookies</h4>
                    <span className="text-[10px] uppercase tracking-[0.18em] text-al-gold">Always active</span>
                  </div>
                  <p className="text-sm text-al-cream/60">Essential for the store to work — your bag, sign-in and checkout. These cannot be disabled.</p>
                </div>

                <div className={rowClass}>
                  <div className="mb-1.5 flex items-center justify-between">
                    <h4 className="text-sm font-medium">Analytics cookies</h4>
                    {toggle(preferences.analytics, (v) => setPreferences({ ...preferences, analytics: v }), 'Analytics cookies')}
                  </div>
                  <p className="text-sm text-al-cream/60">Help us understand how visitors use the store, using anonymous data.</p>
                </div>

                <div className={rowClass}>
                  <div className="mb-1.5 flex items-center justify-between">
                    <h4 className="text-sm font-medium">Marketing cookies</h4>
                    {toggle(preferences.marketing, (v) => setPreferences({ ...preferences, marketing: v }), 'Marketing cookies')}
                  </div>
                  <p className="text-sm text-al-cream/60">Used to show relevant advertising and measure campaign performance.</p>
                </div>

                <div className={rowClass}>
                  <div className="mb-1.5 flex items-center justify-between">
                    <h4 className="text-sm font-medium">Preference cookies</h4>
                    {toggle(preferences.preferences, (v) => setPreferences({ ...preferences, preferences: v }), 'Preference cookies')}
                  </div>
                  <p className="text-sm text-al-cream/60">Remember your settings for a more personal experience.</p>
                </div>
              </div>

              <div className="flex flex-wrap gap-2.5">
                <button onClick={savePreferences} className="al-btn-primary px-5 py-2.5">
                  Save preferences
                </button>
                <button onClick={acceptAll} className="al-btn-ghost px-5 py-2.5">
                  Accept all
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

// Extend Window interface for gtag
declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
  }
}
